import { useState, useEffect } from 'react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { getRandomKeywords } from '@creanizer/utils'
import {
  GeneratorForm,
  GenerateButton,
  SeedInput,
  Slider,
  Checkbox,
  Label,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Textarea,
  ResultList,
  ResultItem,
  EmptyState,
  Alert,
  AlertDescription,
  Progress,
} from '@creanizer/ui'
import { Dices, RefreshCw, Save, Download, Archive, Trash2, FileText, ChevronDown, ChevronUp, Star, Eye, Edit3 } from 'lucide-react'
import { ExportImportButtons } from '../../../../components/common'
import type {
  PlotGenre,
  PlotStage,
  PlotKeyword,
  PlotResult,
  PlotInput,
  SavedPlot,
  StoryLength,
  StoryLengthChapters,
  PlotMode,
  ChapterDetailLevel,
} from '@creanizer/types'
import type { CharacterInput, CharacterResult, SavedCharacter } from '../../../../types/character' // temp fix
import { PlotDetail } from './PlotDetail'
import { StandardModeForm } from './StandardModeForm'
import { IdeaModeForm } from './IdeaModeForm'
import { PlotGeneratorForm } from './PlotGeneratorForm'
import { PlotEditor } from './PlotEditor'
import { EditModeContainer } from './EditModeContainer'
import { SavedPlotsList } from './SavedPlotsList'

const genres: Record<PlotGenre | string, string> = {
  action: 'アクション',
  suspense: 'サスペンス',
  horror: 'ホラー',
  comedy: 'コメディ',
  romance: 'ラブロマンス',
  fantasy: 'ファンタジー',
  sf: 'SF',
  mystery: 'ミステリー',
  slice_of_life: '日常系',
  thriller: 'スリラー',
  adventure: '冒険',
  drama: 'ドラマ',
}

const stages: Record<PlotStage | string, string> = {
  modern_japan: '現代日本',
  isekai_fantasy: '異世界ファンタジー',
  near_future: '近未来',
  sf_space: 'SF宇宙',
  medieval_europe: '中世ヨーロッパ',
  edo_japan: '江戸時代',
  post_apocalypse: 'ポストアポカリプス',
  cyberpunk: 'サイバーパンク',
  steampunk: 'スチームパンク',
  underwater: '海底都市',
  floating_city: '浮遊都市',
  virtual_reality: '仮想現実',
}

export function PlotGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const generator = useGenerator('plot')
  const streamGenerator = useStreamGenerator('plot')
  const { savedWorlds } = useWorldBuilderStore()
  const { dungeons } = useDungeonStore()
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  const { items } = useItemStore()
  const { savedCommunities } = useCommunityStore()
  const { savedSocieties } = useSocietyStore()
  
  // メインタブ状態（ジェネレータと保存済み）
  const [mainTab, setMainTab] = useState<'generator' | 'saved'>('generator')
  
  // モード選択とタブ状態
  const [mode, setMode] = useState<PlotMode>('standard')
  const [activeTab, setActiveTab] = useState<'standard' | 'idea' | 'edit'>('standard')
  
  // storeからの購読を最適化 - savedPlotsは別途取得
  const savePlot = usePlotStore(state => state.savePlot)
  const deletePlot = usePlotStore(state => state.deletePlot)
  const exportPlot = usePlotStore(state => state.exportPlot)
  const exportAsText = usePlotStore(state => state.exportAsText)
  const exportAsJSON = usePlotStore(state => state.exportAsJSON)
  const importFromJSON = usePlotStore(state => state.importFromJSON)
  const clearAllPlots = usePlotStore(state => state.clearAllPlots)
  const isPlotSaved = usePlotStore(state => state.isPlotSaved)
  const savedPlotsCount = usePlotStore(state => state.savedPlots.length)
  
  // レンダリング追跡用のuseEffect
  useEffect(() => {
    console.log('[PlotGenerator] Component rendered')
  })
  
  // デバッグログ
  useEffect(() => {
    console.log('[PlotGenerator] activeTab changed:', activeTab)
  }, [activeTab])
  
  useEffect(() => {
    console.log('[PlotGenerator] savedPlotsCount changed:', savedPlotsCount)
  }, [savedPlotsCount])
  
  // 保存済みプロット一覧表示時のみ取得
  const allSavedPlots = usePlotStore(state => state.savedPlots)
  const savedPlots = (mainTab === 'saved' || activeTab === 'edit') ? allSavedPlots : []
  
  // allSavedPlotsの変更を追跡
  useEffect(() => {
    console.log('[PlotGenerator] allSavedPlots changed, count:', allSavedPlots.length)
  }, [allSavedPlots])
  
  // ジャンル
  const [genreMode, setGenreMode] = useState<'select' | 'custom'>('select')
  const [genre, setGenre] = useState<PlotGenre>('fantasy')
  const [genreCustom, setGenreCustom] = useState('')
  
  // 舞台
  const [stageMode, setStageMode] = useState<'select' | 'custom'>('select')
  const [stage, setStage] = useState<PlotStage>('isekai_fantasy')
  const [stageCustom, setStageCustom] = useState('')
  
  // キーワード
  const [keywords, setKeywords] = useState<PlotKeyword[]>([])
  const [keywordModes, setKeywordModes] = useState<('random' | 'custom')[]>(['random', 'random', 'random'])
  
  // アイディアモード
  const [ideaText, setIdeaText] = useState('')
  
  // 標準モード用の世界観・要素選択
  const [standardSelectedWorldId, setStandardSelectedWorldId] = useState<string | null>(null)
  const [standardSelectedDungeonId, setStandardSelectedDungeonId] = useState<string | null>(null)
  const [standardSelectedCharacterIds, setStandardSelectedCharacterIds] = useState<string[]>([])
  const [standardSelectedEnemyIds, setStandardSelectedEnemyIds] = useState<string[]>([])
  const [standardSelectedItemIds, setStandardSelectedItemIds] = useState<string[]>([])
  const [standardSelectedCommunityIds, setStandardSelectedCommunityIds] = useState<string[]>([])
  const [standardSelectedSocietyIds, setStandardSelectedSocietyIds] = useState<string[]>([])
  
  // 追加設定
  const [additionalSettings, setAdditionalSettings] = useState('')
  
  // その他の設定
  const [creativityLevel, setCreativityLevel] = useState(5)
  const [storyLength, setStoryLength] = useState<StoryLength>('medium')
  const [chapterDetailLevel, setChapterDetailLevel] = useState<ChapterDetailLevel>('standard')
  const [seed, setSeed] = useState<number | undefined>()
  const [useProMode, setUseProMode] = useState(false)
  const [useGemini3FlashMode, setUseGemini3FlashMode] = useState(false)
  const [useStreaming, setUseStreaming] = useState(true)
  
  // 結果
  const [results, setResults] = useState<{ result: PlotResult; input: PlotInput }[]>([])
  const [selectedPlot, setSelectedPlot] = useState<SavedPlot | null>(null)
  const [editingPlotId, setEditingPlotId] = useState<string | null>(null)
  
  // エラー状態
  const [error, setError] = useState<string | null>(null)

  // 編集中のプロットID（編集ボタンからの遷移用）

  // 初期キーワードの設定
  useEffect(() => {
    if (keywords.length === 0) {
      const initialKeywords = getRandomKeywords(3)
      setKeywords(initialKeywords.map((value, index) => ({
        value,
        isCustom: false,
        index,
      })))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // キーワードの再抽選
  const rerollKeyword = (index: number) => {
    const newKeyword = getRandomKeywords(1, Date.now() + index)[0]
    const newKeywords = [...keywords]
    newKeywords[index] = {
      value: newKeyword,
      isCustom: false,
      index: Math.floor(Math.random() * 700), // キーワード総数
    }
    setKeywords(newKeywords)
  }

  // キーワードモードの切り替え
  const toggleKeywordMode = (index: number) => {
    const newModes = [...keywordModes]
    newModes[index] = newModes[index] === 'random' ? 'custom' : 'random'
    setKeywordModes(newModes)
    
    if (newModes[index] === 'random') {
      rerollKeyword(index)
    } else {
      const newKeywords = [...keywords]
      newKeywords[index] = {
        value: '',
        isCustom: true,
      }
      setKeywords(newKeywords)
    }
  }

  // カスタムキーワードの更新
  const updateCustomKeyword = (index: number, value: string) => {
    const newKeywords = [...keywords]
    newKeywords[index] = {
      value,
      isCustom: true,
    }
    setKeywords(newKeywords)
  }

  // 生成処理
  const handleGenerate = async () => {
    console.log('Generating plot with mode:', mode)
    setError(null) // エラーをクリア
    
    try {
      // 標準モードのパラメータ構築
      let standardParams: PlotInput | undefined
      if (mode === 'standard') {
        // 世界観が選択されている場合、ideaTextとして世界観JSONを含める
        let ideaTextForStandard = ''
        if (standardSelectedWorldId) {
          const selectedWorld = savedWorlds.find(w => w.id === standardSelectedWorldId)
          if (selectedWorld) {
            ideaTextForStandard = `【世界観：${selectedWorld.result.name}】\n${JSON.stringify(selectedWorld.result)}`
          }
        }
        
        // ダンジョンが選択されている場合、ideaTextにダンジョンJSONも含める
        if (standardSelectedDungeonId) {
          const selectedDungeon = dungeons.find(d => d.id === standardSelectedDungeonId)
          if (selectedDungeon) {
            ideaTextForStandard += `\n\n【ダンジョン：${selectedDungeon.result.basicInfo.name}】\n${JSON.stringify(selectedDungeon.result)}`
          }
        }

        // コンテキストの構築
        const characterContexts = standardSelectedCharacterIds.map(id => {
          const char = characters.find(c => c.id === id)
          if (!char) return null
          return {
            id,
            name: char.result.name,
            role: 'supporting' as const,
            data: JSON.stringify(char.result)
          }
        }).filter(Boolean) as any[]

        const enemyContexts = standardSelectedEnemyIds.map(id => {
          const enemy = enemies.find(e => e.id === id)
          if (!enemy) return null
          return {
            id,
            name: enemy.result.basicInfo.name,
            role: 'threat' as const,
            data: JSON.stringify(enemy.result)
          }
        }).filter(Boolean) as any[]

        const itemContexts = standardSelectedItemIds.map(id => {
          const item = items.find(i => i.id === id)
          if (!item) return null
          return {
            id,
            name: item.result.basicInfo.name,
            usage: 'mentioned' as const,
            data: JSON.stringify(item.result)
          }
        }).filter(Boolean) as any[]

        const communityContexts = standardSelectedCommunityIds.map(id => {
          const community = savedCommunities.find(c => c.id === id)
          if (!community) return null
          return {
            id,
            name: community.result.basicInfo.name,
            type: community.result.basicInfo.type,
            data: JSON.stringify(community.result)
          }
        }).filter(Boolean) as any[]

        const societyContexts = standardSelectedSocietyIds.map(id => {
          const society = savedSocieties.find(s => s.id === id)
          if (!society) return null
          return {
            id,
            name: society.result.basicInfo.name,
            type: society.result.basicInfo.type,
            data: JSON.stringify(society.result)
          }
        }).filter(Boolean) as any[]

        standardParams = {
          mode: 'standard',
          genre: standardSelectedWorldId ? undefined : (genreMode === 'select' ? genre : genreCustom),
          genreCustom: standardSelectedWorldId ? undefined : (genreMode === 'custom' ? genreCustom : undefined),
          stage: standardSelectedWorldId ? undefined : (stageMode === 'select' ? stage : stageCustom),
          stageCustom: standardSelectedWorldId ? undefined : (stageMode === 'custom' ? stageCustom : undefined),
          keywords,
          ideaText: ideaTextForStandard || undefined,
          characterContexts: characterContexts.length > 0 ? characterContexts : undefined,
          enemyContexts: enemyContexts.length > 0 ? enemyContexts : undefined,
          itemContexts: itemContexts.length > 0 ? itemContexts : undefined,
          communityContexts: communityContexts.length > 0 ? communityContexts : undefined,
          societyContexts: societyContexts.length > 0 ? societyContexts : undefined,
          additionalSettings: additionalSettings.trim() || undefined,
          creativityLevel,
          storyLength,
          chapterDetailLevel,
          seed,
          useProMode,
          useGemini3FlashMode,
        }
      }

      const params: PlotInput = mode === 'standard' ? standardParams! : {
        mode: 'idea',
        ideaText,
        creativityLevel,
        storyLength,
        chapterDetailLevel,
        seed,
        useProMode,
        useGemini3FlashMode,
      }
      
      if (useStreaming) {
        await streamGenerator.generateStream(params, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              const newResult = resultData as PlotResult
              const plotInput: PlotInput = {
                genre: genreMode === 'select' ? genre : genreCustom,
                genreCustom: genreMode === 'custom' ? genreCustom : undefined,
                stage: stageMode === 'select' ? stage : stageCustom,
                stageCustom: stageMode === 'custom' ? stageCustom : undefined,
                keywords,
                creativityLevel,
                storyLength,
                chapterDetailLevel,
                seed,
                useProMode,
              }
              setResults([{ result: newResult, input: plotInput }, ...results])
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(params)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(params)
        
        console.log('Response:', response)
        
        if (response.result) {
          const newResult = response.result as PlotResult
          const plotInput: PlotInput = {
            genre: genreMode === 'select' ? genre : genreCustom,
            genreCustom: genreMode === 'custom' ? genreCustom : undefined,
            stage: stageMode === 'select' ? stage : stageCustom,
            stageCustom: stageMode === 'custom' ? stageCustom : undefined,
            keywords,
            creativityLevel,
            storyLength,
            chapterDetailLevel,
            seed,
            useProMode,
          }
          setResults([{ result: newResult, input: plotInput }, ...results])
        }
      }
    } catch (error: any) {
      console.error('Generation failed:', error)
      
      // エラーメッセージを設定
      if (error.message?.includes('Empty response')) {
        setError('生成に失敗しました。もう一度お試しください。')
      } else if (error.message?.includes('rate limit')) {
        setError('APIの利用制限に達しました。しばらく待ってから再試行してください。')
      } else if (error.message?.includes('parse')) {
        setError('生成結果の処理に失敗しました。もう一度お試しください。')
      } else if (error.message?.includes('API key')) {
        setError('APIキーが設定されていないか、無効です。')
      } else {
        setError('エラーが発生しました: ' + (error.message || '不明なエラー'))
      }
    }
  }

  const canGenerate = isApiKeySet() && (
    mode === 'standard' ? (
      (genreMode === 'select' || genreCustom) &&
      (stageMode === 'select' || stageCustom) &&
      keywords.every(k => k.value)
    ) : (
      ideaText.trim().length > 0
    )
  )

  return (
    <div className="space-y-6">
      {!isApiKeySet() && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <AlertDescription className="text-yellow-800">
            ⚠️ Gemini APIキーが設定されていません。ヘッダーのボタンから設定してください。
          </AlertDescription>
        </Alert>
      )}

      {/* メインタブ */}
      <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as 'generator' | 'saved')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generator">ジェネレータ</TabsTrigger>
          <TabsTrigger value="saved" disabled={savedPlotsCount === 0}>
            保存済み ({savedPlotsCount})
          </TabsTrigger>
        </TabsList>

        {/* ジェネレータタブ */}
        <TabsContent value="generator" className="space-y-6">
      <GeneratorForm 
        title="プロットジェネレータ" 
        description={activeTab === 'edit' ? 
          "保存されたプロットを編集中です" :
          mode === 'standard' ? 
            "ジャンル・舞台・キーワードを組み合わせて物語のプロットを生成します" :
            "アイディアや要素から完成されたプロットを生成します"
        }
      >
        {/* モード選択 */}
        <div className="space-y-2">
          <Tabs value={activeTab} onValueChange={(v) => {
            console.log('[PlotGenerator] Tab change requested:', v, 'current activeTab:', activeTab)
            const tabValue = v as 'standard' | 'idea' | 'edit'
            setActiveTab(tabValue)
            
            if (v === 'edit') {
              console.log('[PlotGenerator] Switching to edit mode')
              // 編集タブが選択された場合の処理（EditModeContainerが処理）
            } else {
              console.log('[PlotGenerator] Switching away from edit mode to:', v)
              setEditingPlotId(null)
              if (v === 'standard' || v === 'idea') {
                setMode(v as PlotMode)
              }
            }
          }}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="standard">標準モード</TabsTrigger>
              <TabsTrigger value="idea">アイディアモード</TabsTrigger>
              <TabsTrigger value="edit" disabled={savedPlotsCount === 0}>
                編集 {savedPlotsCount > 0 && `(${savedPlotsCount})`}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* 編集モード */}
        {activeTab === 'edit' && (
          <EditModeContainer 
            initialPlotId={editingPlotId}
            onClose={() => {
              setEditingPlotId(null)
              setActiveTab('standard')
            }}
          />
        )}

        {/* モード別フォーム */}
        {activeTab !== 'edit' && mode === 'standard' ? (
          <StandardModeForm
            genreMode={genreMode}
            setGenreMode={setGenreMode}
            genre={genre}
            setGenre={setGenre}
            genreCustom={genreCustom}
            setGenreCustom={setGenreCustom}
            stageMode={stageMode}
            setStageMode={setStageMode}
            stage={stage}
            setStage={setStage}
            stageCustom={stageCustom}
            setStageCustom={setStageCustom}
            keywords={keywords}
            setKeywords={setKeywords}
            keywordModes={keywordModes}
            setKeywordModes={setKeywordModes}
            selectedWorldId={standardSelectedWorldId}
            setSelectedWorldId={setStandardSelectedWorldId}
            selectedDungeonId={standardSelectedDungeonId}
            setSelectedDungeonId={setStandardSelectedDungeonId}
            selectedCharacterIds={standardSelectedCharacterIds}
            setSelectedCharacterIds={setStandardSelectedCharacterIds}
            selectedEnemyIds={standardSelectedEnemyIds}
            setSelectedEnemyIds={setStandardSelectedEnemyIds}
            selectedItemIds={standardSelectedItemIds}
            setSelectedItemIds={setStandardSelectedItemIds}
            selectedCommunityIds={standardSelectedCommunityIds}
            setSelectedCommunityIds={setStandardSelectedCommunityIds}
            selectedSocietyIds={standardSelectedSocietyIds}
            setSelectedSocietyIds={setStandardSelectedSocietyIds}
          />
        ) : activeTab !== 'edit' && mode === 'idea' ? (
          <IdeaModeForm
            ideaText={ideaText}
            setIdeaText={setIdeaText}
            savedCommunities={savedCommunities}
            savedSocieties={savedSocieties}
          />
        ) : null}

        {/* 追加設定（標準モードのみ） */}
        {activeTab !== 'edit' && mode === 'standard' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="additionalSettings">追加設定（任意）</Label>
              <span className="text-xs text-muted-foreground">
                {additionalSettings ? `${additionalSettings.length}文字` : ''}
              </span>
            </div>
            <Textarea
              id="additionalSettings"
              value={additionalSettings}
              onChange={(e) => setAdditionalSettings(e.target.value)}
              placeholder="プロットに関する追加の要望や設定があれば記入してください（例：必ずハッピーエンド、伏線を多めに、どんでん返しがあるなど）"
              rows={3}
              className="text-sm"
            />
            <p className="text-xs text-muted-foreground">
              ここに記載した内容は生成時に考慮されます（記入しなくても生成可能です）
            </p>
          </div>
        )}

        {/* 共通設定 */}
        {activeTab !== 'edit' && (
          <>
            <PlotGeneratorForm
              creativityLevel={creativityLevel}
              setCreativityLevel={setCreativityLevel}
              storyLength={storyLength}
              setStoryLength={setStoryLength}
              chapterDetailLevel={chapterDetailLevel}
              setChapterDetailLevel={setChapterDetailLevel}
              seed={seed}
              setSeed={setSeed}
              useProMode={useProMode}
              setUseProMode={setUseProMode}
              useGemini3FlashMode={useGemini3FlashMode}
              setUseGemini3FlashMode={setUseGemini3FlashMode}
            />

            {/* ストリーミング設定 */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useStreaming"
                checked={useStreaming}
                onCheckedChange={(checked) => setUseStreaming(checked as boolean)}
              />
              <Label htmlFor="useStreaming" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                ストリーミングを使用（リアルタイム生成・進捗表示）
              </Label>
            </div>

            {/* 生成ボタン */}
            <GenerateButton
              onClick={handleGenerate}
              isLoading={generator.isPending || streamGenerator.isStreaming}
              disabled={!canGenerate}
            >
              プロットを生成
            </GenerateButton>
          </>
        )}
        
        {/* エラーメッセージ */}
        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </GeneratorForm>

      {/* ストリーミング進捗表示 */}
      {streamGenerator.isStreaming && (
        <div className="card">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>生成中...</span>
              <span>{Math.round(streamGenerator.progress)}%</span>
            </div>
            <Progress value={streamGenerator.progress} />
            {streamGenerator.partialContent && (
              <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                <pre className="text-xs whitespace-pre-wrap">
                  {streamGenerator.partialContent.slice(-500)}
                  {streamGenerator.partialContent.length > 500 && '...'}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}


      {/* 結果表示 */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">生成結果</h3>
        {results.length === 0 ? (
          <EmptyState
            icon="sparkles"
            title="まだプロットが生成されていません"
            description="上のフォームから設定を選択して、プロットを生成してください"
          />
        ) : (
          <ResultList>
            {results.map((item, index) => {
              const { result, input: plotInput } = item
              const isSaved = isPlotSaved(result.title)
              
              return (
              <div key={index} className="space-y-4 p-4 border rounded-lg">
                {/* タイトルとアクション */}
                <div className="flex items-start justify-between">
                  <h4 className="text-xl font-bold">{result.title}</h4>
                  <div className="flex gap-2">
                    {!isSaved && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const id = savePlot(plotInput, result)
                          if (id) {
                            alert('プロットを保存しました')
                          }
                        }}
                      >
                        <Star className="h-4 w-4 mr-2" />
                        保存
                      </Button>
                    )}
                    {isSaved && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                      >
                        <Star className="h-4 w-4 mr-2 fill-yellow-400 text-yellow-400" />
                        保存済み
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const tempPlot: SavedPlot = {
                          id: 'temp-' + index,
                          title: result.title,
                          input: plotInput,
                          result,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                        }
                        setSelectedPlot(tempPlot)
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      詳細
                    </Button>
                  </div>
                </div>

                {/* 前提 */}
                <div>
                  <h5 className="text-sm font-medium text-gray-600 mb-1">前提</h5>
                  <p className="text-sm">{result.premise}</p>
                </div>

                {/* あらすじ */}
                <div>
                  <h5 className="text-sm font-medium text-gray-600 mb-1">あらすじ</h5>
                  <p className="text-sm whitespace-pre-wrap">{result.synopsis}</p>
                </div>

                {/* 章構成 */}
                {result.structure && result.structure.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-600 mb-2">章構成</h5>
                    <div className="space-y-2">
                      {result.structure.map((chapter, i) => (
                        <div key={i} className="border-l-2 border-blue-200 pl-3">
                          <h6 className="font-medium text-sm">
                            第{chapter.number}章: {chapter.title}
                          </h6>
                          <p className="text-xs text-gray-600 mt-1">{chapter.summary}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 登場人物 */}
                {result.characters && result.characters.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-600 mb-2">主要登場人物</h5>
                    <div className="space-y-1">
                      {result.characters.map((char, i) => (
                        <div key={i} className="flex gap-2 text-sm">
                          <span className="font-medium">{char.name}:</span>
                          <span className="text-gray-600">{char.role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* テーマ */}
                {result.themes && result.themes.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-600 mb-1">テーマ</h5>
                    <div className="flex flex-wrap gap-2">
                      {result.themes.map((theme, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs">
                          {theme}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )
            })}
          </ResultList>
        )}
      </div>

      {/* プロット詳細モーダル */}
      {selectedPlot && (
        <PlotDetail
          plot={selectedPlot}
          onClose={() => setSelectedPlot(null)}
        />
      )}
        </TabsContent>

        {/* 保存済みタブ */}
        <TabsContent value="saved">
          <SavedPlotsList 
            onLoad={(plot) => {
              setSelectedPlot(plot)
              setMainTab('generator')
            }}
            onEdit={(plotId) => {
              setEditingPlotId(plotId)
              setActiveTab('edit')
              setMainTab('generator')
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}